import styled, { css } from 'styled-components';
import { Colors, Spacings } from 'styles';

const alignments = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  baseline: 'baseline',
  between: 'space-between',
} as const;

const shouldForwardProp = (prop: string, defaultValidatorFn?: ((prop: string) => boolean) | null) =>
  (!['vertical', 'stretchY', 'stretchX', 'space', 'padding', 'alignX', 'alignY', 'wrap'].includes(
    prop
  ) &&
    defaultValidatorFn?.(prop)) ??
  true;

export const Spacer = styled.div`
  flex: 1;
`;

type DividerProps = {
  vertical: boolean;
};

export const Divider = styled.div.attrs({
  vertical: false,
})`
  width: ${(props: DividerProps) => (props.vertical ? 'auto' : '1px')};
  height: ${(props: DividerProps) => (props.vertical ? '1px' : 'auto')};
  align-self: stretch;
  background-color: ${Colors.gray5};
`;

type StackProps = {
  children: Nullable<React.ReactNode>;
  className?: string;
  vertical: boolean;
  stretchY: boolean;
  stretchX: boolean;
  padding: keyof typeof Spacings;
  space: keyof typeof Spacings;
  alignX: keyof typeof alignments;
  alignY: keyof typeof alignments;
  wrap: boolean;
};

export const Stack = styled.div
  .withConfig({
    // @ts-expect-error [EN-10263] - TS2322 - Type '(prop: string, defaultValidatorFn?: ((prop: string) => boolean) | null) => boolean' is not assignable to type '(prop: string | number, defaultValidatorFn: (prop: string | number) => boolean) => boolean'.
    shouldForwardProp,
  })
  // @ts-expect-error [EN-7967] - TS2339 - Property 'wrap' does not exist on type 'Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "ref"> & { ref?: Ref<HTMLDivElement>; } & ThemeProps<...>'.
  .attrs(({ wrap, ...props }) => ({
    vertical: false,
    stretchY: false,
    stretchX: false,
    space: 'none',
    padding: 'none',
    alignX: 'stretch',
    alignY: 'stretch',
    ...props,
  }))`
  display: flex;
  flex-wrap: ${(props: StackProps) => (props.wrap ? 'wrap' : 'nowrap')};
  justify-content: ${(props: StackProps) => alignments[props.alignX]};
  align-items: ${(props: StackProps) => alignments[props.alignY]};
  height: ${(props: StackProps) => (props.stretchY ? '100%' : 'auto')};
  align-self: ${(props: StackProps) => (props.stretchX ? 'stretch' : 'initial')};
  padding: ${(props: StackProps) => Spacings[props.padding]}rem;

  > *:not(:last-child) {
    margin-right: ${(props: StackProps) => Spacings[props.space]}rem;
  }

  ${(props: StackProps) =>
    props.vertical &&
    css`
      flex-direction: column;
      justify-content: ${(props: StackProps) => alignments[props.alignY]};
      align-items: ${(props: StackProps) => alignments[props.alignX]};

      > *:not(:last-child) {
        margin-right: 0;
        margin-bottom: ${(props: StackProps) => Spacings[props.space]}rem;
      }
    `};

  [data-stack-stretch] {
    flex: 1;
  }

  > * {
    margin: 0;
  }
`;

type GridOptionsOptional = React.HTMLAttributes<HTMLDivElement> & {
  className: string;
  padding: keyof typeof Spacings;
  space: keyof typeof Spacings;
  spaceX: keyof typeof Spacings;
  spaceY: keyof typeof Spacings;
  alignX: keyof typeof alignments;
  alignY: keyof typeof alignments;
  stretchY: boolean;
};

type GridProps = {
  children: React.ReactNode;
  columns: string[];
  rows: string[];
} & Partial<GridOptionsOptional>;

export const Grid = styled.div
  .withConfig({
    shouldForwardProp: (prop) =>
      ![
        'space',
        'spaceX',
        'spaceY',
        'padding',
        'alignX',
        'alignY',
        'columns',
        'rows',
        'stretchY',
      ].includes(prop),
  })
  .attrs((props) => ({
    space: 'none',
    padding: 'none',
    alignX: 'stretch',
    alignY: 'stretch',
    columns: [] as string[],
    rows: [] as string[],
    ...props,
  }))<GridProps>`
  display: grid;

  justify-content: ${(props: GridProps) => (props.alignX ? alignments[props.alignX] : 'stretch')};
  align-items: ${(props: GridProps) => (props.alignY ? alignments[props.alignY] : 'stretch')};
  grid-gap: ${(props: GridProps) => Spacings[props.space ?? 'none']}rem;
  ${(props: GridProps) =>
    props.spaceY != null ? `grid-row-gap: ${Spacings[props.spaceY]}rem;` : ''}
  ${(props: GridProps) =>
    props.spaceX != null ? `grid-column-gap: ${Spacings[props.spaceX]}rem;` : ''}
  grid-template-columns: ${(props: GridProps) => props.columns.join(',')};
  grid-template-rows: ${(props: GridProps) => props.rows.join(',')};
  padding: ${(props: GridProps) => Spacings[props.padding ?? 'none']}rem;
`;
